// ============================================================================
// L'HISTORIQUE D'UN APPAREIL — les clients et les magasins où il est passé, et ce qu'on lui a fait.
//
// UTILITÉ : la fiche d'un appareil ne garde que son affectation courante ; son passé vit dans le journal de la Console,
// rattaché à l'appareil (`device-history.ts` côté serveur). Ouverte depuis les Appareils et l'onglet Équipements de la
// fiche magasin.
// ⚠️ L'HISTORIQUE COMMENCE À UNE DATE (`since`) et l'écran la DIT : un appareil plus ancien n'a rien avant elle, et une
//    liste courte se lirait « il n'a jamais servi ailleurs ».
// ⚠️ UNE LECTURE EN ÉCHEC SE DIT avec son motif, jamais « aucun évènement » (§23.0).
// ============================================================================
import { useEffect, useId, useState, type MouseEvent } from 'react';
import { dateCompact, dateTimeLong } from '@orgazone/date-format';
import { EmptyState, useOverlayPanel } from '@orgazone/ui-web';
import { api, type DeviceHistoryEvent } from '../api';
import { glassBoxSt, glassOverlaySt } from '../glass-modal';
import { deviceRef } from '../device-name';

/** Ce que la fenêtre a besoin de savoir de l'appareil : de quoi le nommer, et son identifiant. */
export interface HistoryTarget { id: string; name: string | null; family: string; model: string | null; serial: string | null }

export function DeviceHistoryModal({ dev, onClose }: { dev: HistoryTarget; onClose: () => void }) {
  const [rows, setRows] = useState<DeviceHistoryEvent[] | null>(null);
  const [since, setSince] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const titleId = useId();
  const { panelRef } = useOverlayPanel({ open: true, onClose });

  useEffect(() => {
    let vivant = true;
    api.deviceHistory(dev.id)
      .then((r) => { if (vivant) { setRows(r.events); setSince(r.since); } })
      .catch((e) => { if (vivant) setErr((e as Error).message); });
    return () => { vivant = false; };
  }, [dev.id]);

  const stop = (e: MouseEvent) => e.stopPropagation();
  const depuis = since ? dateCompact(new Date(`${since}T12:00:00`)) : null;

  return (
    <div style={glassOverlaySt} onClick={onClose}>
      <div style={{ ...glassBoxSt(640), outline: 'none' }} ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={stop}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--ink200)', background: 'var(--head)', display: 'flex', alignItems: 'flex-start', gap: 14, flexShrink: 0 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink500)' }}>
              {[deviceRef(dev), dev.model ?? dev.family, dev.serial ? `SN ${dev.serial}` : null].filter(Boolean).join(' · ')}
            </div>
            <h3 id={titleId} style={{ margin: '5px 0 0', fontSize: 16, fontWeight: 700, letterSpacing: '-.02em' }}>Historique de l’appareil</h3>
            <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5, textWrap: 'pretty' }}>
              Les clients et les magasins où il est passé, et ce que la Console lui a fait — du plus récent au plus ancien.
              {depuis && ` Suivi depuis le ${depuis} : rien n’est connu avant.`}
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} title="Fermer" aria-label="Fermer">✕</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {err ? (
            <div className="err" style={{ margin: 18 }}>Historique indisponible : {err}</div>
          ) : !rows ? (
            <div className="muted" style={{ padding: 18 }}>Lecture de l’historique…</div>
          ) : rows.length === 0 ? (
            <EmptyState compact icon="clock" title="Aucun évènement suivi"
              note={depuis ? `L’appareil n’a rien fait depuis le ${depuis}, date à laquelle le suivi commence.` : 'Aucun évènement n’est rattaché à cet appareil.'} />
          ) : (
            <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {rows.map((e, i) => (
                <li key={`${e.at}-${i}`} style={{ padding: '12px 22px', borderBottom: '1px solid var(--ink100)' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink900)', flex: '1 1 300px' }}>{e.text}</span>
                    <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{dateTimeLong(e.at)}</span>
                  </div>
                  {e.meta && <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.5 }}>{e.meta}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
